// =================================================================
// emotionalTestimonials.js — Rich, hand-crafted story fragments for
// the 1000 additional testimonials focused on Magia Suprema and
// Poder Máximo, with distinct archetypes:
//   embarazo · salud_grave · quiebra · desaparecido · adiccion ·
//   proteccion · amor_imposible · milagro
//
// Gender-aware: each name gets tagged fem/masc so tone matches.
// Regional flavor: a subtle modismo is injected by country style.
//
// Pool sizes are PRIMES and PAIRWISE COPRIME so a coprime-multiplier
// enumeration guarantees uniqueness across the 1000 comments.
// =================================================================

// --- Gender detection from first name (works for MX/LATAM/US-ES/CA) ---
// Female-ending letters set (spanish) — a-ending is female for names.
const FEMALE_ENDINGS = ["a", "é"]; // "é" catches "Renée", etc.
const FEMALE_NAMES = new Set([
  "María","Guadalupe","Cristina","Esmeralda","Brenda","Adriana","Mariana",
  "Gabriela","Erika","Yesenia","Diana","Karla","Verónica","Liliana",
  "Susana","Norma","Beatriz","Margarita","Lorena","Yolanda","Rosa",
  "Carmen","Leticia","Alejandra","Rocío","Regina","Sofía","Renata",
  "Sofía","Valentina","Camila","Martina","Isabella","Antonella",
  "Emilia","Florencia","Constanza","Pilar","Macarena","Julieta",
  "Carolina","Chloé","Léa","Camille","Élodie","Gabrielle","Rosalie",
  "Manon","Isabelle","Nadine","Catherine","Nicole","Sylvie","Emily",
  "Olivia","Sophia","Charlotte","Amelia","Bella","Anna","Elena",
  "Giulia","Sophie","Antoine","Beatriz","Ashley","Jessica","Amanda",
  "Stephanie","Jennifer","Nicole","Michelle","Melissa","Erica",
  "Diana","Rebecca","Christina","Julia","Rachel","Sarah","Emma",
  "Ariana","Karina","Kathia","Marisol","Yolanda","Concepción",
  "Emma","Frida","Sonia","Bianca","Fatima","Vanessa","Andrea",
  "Aracely","Cecilia","Consuelo","Corina","Cynthia","Dulce",
  "Elizabeth","Fabiola","Fernanda","Georgina","Ileana","Irene",
  "Ivonne","Janeth","Judith","Julieta","Katia","Laura","Lidia",
  "Lourdes","Lucero","Luisa","Magdalena","Maribel","Marlene",
  "Mercedes","Michelle","Miriam","Mónica","Nadia","Nayeli","Nelly",
  "Nydia","Olga","Patricia","Paulina","Perla","Raquel","Reyna",
  "Roxana","Sandra","Silvia","Tania","Teresa","Vanessa","Yasmin",
  "Zulema","Ximena","Daniela","Alma","Alicia","Alba","Angela",
  "Aurora","Bertha","Berenice","Blanca","Claudia","Elva","Elvira",
  "Enriqueta","Ernestina","Estela","Eugenia","Eva","Genoveva","Gloria",
  "Hilda","Josefina","Nancy","Otilia","Ramona","Sara","Socorro",
  "Tania","Tomasa","Trinidad","Verónica","Virginia",
]);
const MALE_NAMES = new Set([
  "José","Juan","Luis","Carlos","Miguel","Jorge","Ricardo","Fernando",
  "Eduardo","Roberto","Héctor","Javier","Rafael","Manuel","Pedro",
  "César","Óscar","Ramón","Salvador","Arturo","Sergio","Mario",
  "Gerardo","Alfredo","Raúl","Daniel","Andrés","Pablo","Iván","Hugo",
  "Esteban","Marco","Antonio","Emilio","Felipe","Cristian","Alan",
  "Bruno","Emiliano","Sebastián","Mateo","Joaquín","Benjamín","Tomás",
  "Nicolás","Agustín","Lautaro","Franco","Maximiliano","Gonzalo",
  "Federico","Lucas","Diego","Ariel","Damián","Gabriel","Justin",
  "Ryan","Nathan","Ethan","Liam","Mason","Louis","Antoine","Mathieu",
  "Jean","Pierre","Marc","Samuel","Alexandre","Benoît","Sébastien",
  "James","Michael","David","Robert","John","William","Christopher",
  "Matthew","Anthony","Mark","Donald","Steven","Paul","Kevin",
  "Brian","George","Edward","Ronald","Timothy","Jason","Jeffrey",
  "Ryan","Jacob","Gary","Nicholas","Eric","Jonathan","Stephen",
  "Larry","Justin","Scott","Brandon","Frank","Benjamin","Gregory",
  "Raymond","Samuel","Patrick","Alexander","Jack","Dennis","Jerry",
  "Tyler","Aaron","Henry","Douglas","José","Joel","Aarón","Abdiel",
  "Abel","Abelardo","Abraham","Adán","Alberto","Alejandro","Alonso",
  "Amado","Ángel","Aníbal","Antonino","Armando","Baltazar","Beto",
  "Camilo","Casimiro","Cirilo","Cornelio","Cristóbal","Dagoberto",
  "Damaso","Delfino","Domingo","Efraín","Elías","Emiliano","Enrique",
  "Ernesto","Ezequiel","Fabián","Fausto","Faustino","Federico","Felix",
  "Fermín","Fidel","Filiberto","Gaspar","Genaro","Guillermo","Gustavo",
  "Hilario","Homero","Horacio","Humberto","Ignacio","Isaac","Isaias",
  "Ismael","Israel","Jaime","Jerónimo","Jesús","Joaquín","José",
  "Julián","Julio","Leonardo","Leonel","Lorenzo","Marcelino","Marcial",
  "Marcos","Martín","Mauricio","Modesto","Moisés","Nabor","Neftalí",
  "Nelson","Nicolás","Noé","Norberto","Octavio","Odilón","Omar",
  "Orlando","Otoniel","Pánfilo","Pascual","Patricio","Plácido",
  "Porfirio","Prisciliano","Ramiro","Rene","Reyes","Reynaldo","Rigoberto",
  "Roque","Rosendo","Rubén","Rufino","Salomón","Santiago","Silverio",
  "Simón","Sixto","Sotero","Susano","Teodoro","Tiburcio","Tomás",
  "Tranquilino","Ulises","Valentín","Vicente","Víctor","Wenceslao",
  "Wilfrido","Yuri","Zacarías",
]);

export const detectGender = (firstName) => {
  if (!firstName) return "masc";
  if (FEMALE_NAMES.has(firstName)) return "fem";
  if (MALE_NAMES.has(firstName)) return "masc";
  const lower = firstName.toLowerCase();
  // Fallback heuristic: name ends in an "a" vowel → likely female
  const lastChar = lower.charAt(lower.length - 1);
  if (FEMALE_ENDINGS.includes(lastChar)) return "fem";
  return "masc";
};

// =================================================================
// ARCHETYPES + BODY SENTENCES (~ prime pool sizes for uniqueness)
// =================================================================

// 8 archetypes with per-story sentences.
// Each pool is 23 entries (prime; 23 × 8 = 184 body variants).
export const BODY_EMBARAZO = [
  "Llevaba siete años intentando quedar embarazada y ya había pasado por tres tratamientos que no funcionaron.",
  "Los doctores me dijeron que era imposible, que mi cuerpo no iba a poder, y sinceramente ya me había resignado.",
  "Después de dos abortos espontáneos había perdido toda la fe, no quería ni escuchar la palabra bebé.",
  "Mi marido y yo cargábamos un dolor grande, cada mes nos rompíamos otra vez.",
  "El maestro me dijo que había una envidia muy fuerte de alguien cercano y que por eso no lograba retener el embarazo.",
  "A las cinco semanas de terminar el trabajo la prueba salió positiva, yo lloré tres días seguidos.",
  "Mi bebé nació sano en marzo y le puse Ángel en agradecimiento al maestro.",
  "Los ginecólogos no entendían cómo, después de once años de infertilidad, quedé embarazada por primera vez.",
  "Fue tan rápido que hasta yo desconfiaba, pero mi hijo lleva ya cuatro meses y está fuerte y hermoso.",
  "Descubrí que estaba embarazada justo el día del cumpleaños de mi mamá, que ya había muerto, sentí que ella me lo mandó.",
  "El maestro fue muy claro, me dijo que primero había que quitar lo que me habían puesto y después pedir con fe.",
  "Tenía miope y endometriosis avanzada, dos médicos me dijeron que la única opción era adoptar.",
  "Cuando el ultrasonido salió con los latidos del corazón, mi esposo se puso a llorar y yo también.",
  "Perdí un bebé en la semana veintiuna, ese dolor me tuvo en cama seis meses, hasta que llegué con él.",
  "Cargaba una tristeza que no dejaba entrar la vida nueva, así me lo explicó, y era verdad.",
  "Al ver la carita de mi hija recién nacida me acordé de él y le mandé un mensaje de agradecimiento a las tres de la mañana.",
  "Nadie de mi familia se explica cómo pasó porque me habían diagnosticado ovarios poliquísticos severos.",
  "Fue una espera larga, casi cinco meses de trabajo espiritual, pero valió cada peso invertido.",
  "Estoy escribiendo esto con mi bebé en brazos, todavía no lo puedo creer.",
  "El maestro me pidió paciencia y confianza, y yo se la di, y ahora tengo a mi gemelas.",
  "Nunca imaginé escribir estas palabras porque veinticinco años me consideraron estéril.",
  "Fue un trabajo pesado, me sentí muy limpia después, y a los dos meses ya venía en camino mi niña.",
  "Después de siete años de matrimonio por fin somos tres, gracias a Dios y al maestro Cris.",
];
export const BODY_SALUD = [
  "Me diagnosticaron cáncer de mama en etapa tres y los doctores no me daban muchas esperanzas.",
  "Mi papá llevaba en coma diecisiete días y los médicos ya nos habían pedido que consideráramos desconectarlo.",
  "Tenía lupus muy avanzado, cada semana me metían al hospital, ya no soportaba tantos medicamentos.",
  "El doctor me dijo textual que era cuestión de meses, que preparara todo, y yo tengo tres hijos pequeños.",
  "Mi hijo de ocho años entró en insuficiencia renal y necesitaba trasplante urgente, yo no tenía cómo pagarlo.",
  "El maestro me dijo que había una atadura muy vieja en mi familia y que primero había que romper eso.",
  "Después del trabajo, en la siguiente resonancia, el tumor había reducido a la mitad y los doctores estaban sorprendidos.",
  "Mi papá abrió los ojos al tercer día del trabajo, no lo puedo explicar de otra manera.",
  "Los análisis salieron limpios por primera vez en cinco años, mi hematólogo dice que es raro pero está.",
  "Encontraron un donante compatible una semana después de terminar el trabajo, cuando ya habíamos perdido la esperanza.",
  "Mi diabetes se estabilizó de una forma que ni el endocrinólogo entiende, me redujeron la insulina a la mitad.",
  "Le habían dado tres meses a mi tía y hoy, dos años después, sigue con nosotros, más fuerte que nunca.",
  "El derrame que le dio a mi mamá se revirtió casi por completo, camina sola y habla claro.",
  "Tenía una fibromialgia que me tenía en silla de ruedas la mitad del mes, hoy hago yoga cuatro veces por semana.",
  "Nací con un problema del corazón que los cardiólogos decían iba a empeorar, en el último eco salió estable.",
  "Mi esposo dejó de fumar de un día para otro después del trabajo, treinta años fumando y ya no quiso más.",
  "Los ataques de ansiedad que llevaba una década tolerando desaparecieron a las tres semanas del trabajo.",
  "El hígado de mi hermano estaba muy dañado, en los últimos análisis los valores bajaron a niveles normales.",
  "La vista de mi mamá regresó parcialmente, ella dice que ve más claro y menos borroso.",
  "Mi niño autista empezó a hablar más y a mirarnos a los ojos, cosas que antes le costaban muchísimo.",
  "El VIH de mi primo se volvió indetectable mucho antes de lo esperado, los infectólogos no lo explican.",
  "Fui a mi última quimioterapia hace ocho meses y salí en remisión completa, todavía se me quiebra la voz al contarlo.",
  "Estaba en diálisis desde hace cuatro años y ahora estoy en la lista para trasplante con posibilidades reales.",
];
export const BODY_QUIEBRA = [
  "Debía casi ochocientos mil pesos entre tarjetas, hipoteca vencida y préstamos personales, ya me llamaban abogados.",
  "Mi negocio de tacos había cerrado seis meses antes y no tenía cómo pagar el local, me iban a demandar.",
  "Perdí mi trabajo en la pandemia y los ahorros se me fueron en gastos médicos de mi mamá.",
  "Me embargaron el carro y estaba a punto de perder la casa que compré con tanto sacrificio.",
  "Iba a declarar quiebra formal, ya tenía todo el papeleo listo con un abogado, cuando escuché del maestro.",
  "El maestro me dijo que había mucha envidia laboral y que por eso todo se me caía en cascada.",
  "A los diez días del trabajo me llamó un cliente antiguo con un contrato grande, me sacó del hoyo.",
  "Recuperé mi puesto en la empresa después de tres años despedido, ni yo lo creía cuando me llamaron.",
  "Vendí una propiedad que llevaba dos años intentando mover, salió a mejor precio del esperado.",
  "Un socio inesperado me buscó para invertir en el negocio, ahora estamos en tres sucursales.",
  "Gané una demanda laboral que llevaba estancada cuatro años, me pagaron todo el retroactivo.",
  "Me subieron de puesto en el trabajo y me ajustaron el sueldo casi al doble, mi jefe dijo que fue por méritos propios.",
  "Un cliente del extranjero me contactó y hoy mi tienda vende para cinco países.",
  "Recibí una herencia de un tío lejano de la que ni sabía, resolvió todas mis deudas.",
  "Mi restaurante estaba a punto de cerrar, en dos meses se llenó y hoy tengo lista de espera los viernes.",
  "El banco me aprobó una reestructura que llevaba pidiendo dos años sin éxito.",
  "Mi ex marido por fin empezó a pagar la manutención, después de siete años de peleas judiciales.",
  "Vendí un carro que llevaba nueve meses parado en el patio, salió un comprador serio al día siguiente del trabajo.",
  "Firmé un contrato de proveedor con una cadena grande, me estabilizó la caja de golpe.",
  "El préstamo que el banco me había negado tres veces, esta vez salió aprobado sin problemas.",
  "Pagué la última letra de mi casa el mes pasado, después de veintidós años con hipoteca.",
  "Mi esposa consiguió un trabajo mejor y entre los dos hoy vivimos tranquilos, algo que llevaba años pidiéndole a Dios.",
  "Empecé un canal de YouTube pequeño y creció tan rápido que hoy vivo de él, jamás lo hubiera imaginado.",
];
export const BODY_DESAPARECIDO = [
  "Mi hijo de dieciséis años se fue de la casa sin avisar y estuvo desaparecido tres semanas.",
  "Mi hermano llevaba nueve meses sin dar señales, la Fiscalía ya no me contestaba las llamadas.",
  "Se llevaron a mi papá los del cartel y no sabíamos si estaba vivo, cargamos ese silencio dos meses.",
  "Mi hija se fue con un novio que no queríamos y no supimos nada durante cinco meses.",
  "Mi primo se perdió cruzando el desierto y llevábamos casi un año sin noticias de él.",
  "El maestro me dijo que estaba vivo, y me dio detalles que no podía saber si no lo hubiera visto de verdad.",
  "Al día doce del trabajo llegó un mensaje de mi hijo, estaba en Tijuana con una amiga.",
  "Mi hermano apareció caminando hacia la casa una mañana, flaco pero completo.",
  "El paradero de mi papá lo dieron autoridades a los tres días de haber terminado el trabajo espiritual.",
  "Mi hija llamó llorando desde un teléfono público, quería regresar y no sabía cómo pedir perdón.",
  "Mi primo llegó por la frontera dos semanas después del trabajo, con la vida entera cambiada.",
  "Fue un consuelo tan grande cuando por fin escuché su voz, me solté a llorar en pleno mercado.",
  "El maestro me describió el lugar exacto donde estaba y no falló en un detalle, todavía me da escalofríos.",
  "Recibimos una llamada del hospital de otro estado, mi tío estaba internado sin identificación.",
  "Mi hijo lo encontró la policía en una gasolinera de Nayarit, exactamente como el maestro había visto.",
  "Un vecino lo vio, lo reconoció y avisó, algo tan simple pero después de tanto tiempo.",
  "Apareció bien, sin heridas, y no supo explicarnos por qué se había ido, dice que se le fue la cabeza.",
  "Cuando abrió la puerta y lo vi entrar corrí sin pensar y lo abracé, no dijimos nada por un minuto.",
  "Mi hermana llegó con las llaves de siempre, como si nada hubiera pasado, con una mochila pequeña.",
  "La familia de mi cuñado recibió una llamada anónima que los llevó directo a donde estaba mi sobrino.",
  "Después de dieciocho meses de búsqueda por fin lo abrazamos, ya lo estábamos considerando muerto.",
  "Mi papá regresó, no habla mucho de lo que pasó pero está aquí, y eso es lo único que importa.",
  "Cuando lo vi bajarse del camión no me salían las palabras, sólo lo abracé y lloré.",
];
export const BODY_PROTECCION = [
  "Mi hija me contó que un tipo la seguía saliendo de la escuela, ya me lo veía en la calle también.",
  "Mi vecino nos amenazó de muerte a mi familia por un problema de una barda, incluso llegó armado.",
  "Un ex socio empezó a mandar gente a molestarnos al negocio, mi esposa temía por los niños.",
  "Una ex pareja obsesiva me mandaba mensajes todos los días, llegó a estacionarse frente a la casa.",
  "Estaba viviendo un acoso laboral tan pesado que ya no dormía, sentía que me iban a inventar algo grave.",
  "El maestro me habló claro, dijo que era una energía muy negra y que había que blindar toda la casa.",
  "Al mes de terminado el trabajo mi acosador se mudó a otra ciudad, sin dar explicaciones.",
  "El vecino que nos amenazó tuvo un accidente de camino y ya no volvió a buscarnos.",
  "El grupo que mi ex socio mandaba desapareció de un día para otro, nunca supimos por qué.",
  "Mi ex pareja perdió el trabajo y con eso perdió las ganas de perseguirme, mudó ciudad y todo.",
  "El proceso laboral que me habían iniciado se archivó por falta de pruebas, salí completamente limpia.",
  "Sentí una tranquilidad en la casa que no sentía desde hace años, los niños volvieron a jugar en el patio.",
  "Mi mamá empezó a dormir toda la noche, antes se despertaba con pesadillas de esas horribles.",
  "Un familiar que nos generaba mucho conflicto se apartó solo, sin discusiones, gracias a Dios.",
  "Descubrí que una compañera de trabajo me estaba trabajando en la brujería mala, el maestro lo confirmó.",
  "Mi negocio empezó a llenarse de nuevo después de meses de que raro nadie entraba.",
  "Ya no siento esa opresión en el pecho al llegar a la casa, es una tranquilidad rara pero linda.",
  "Los niños dejaron de enfermarse cada quince días, algo que llevaba pasando dos años seguidos.",
  "Encontré cosas raras enterradas en el patio y las quemé como me indicó el maestro.",
  "Mi esposo dejó de recibir esas llamadas anónimas nocturnas, hoy dormimos sin miedo.",
  "El acosador de mi hija fue detenido por otra denuncia y salió su historial largo.",
  "Un aliado inesperado en el trabajo dio la cara por mí y la campaña en mi contra se cayó.",
  "Después del trabajo, la energía en casa es otra, mis amigas también lo notan cuando vienen.",
];
export const BODY_ADICCION = [
  "Mi hijo llevaba tres años en las drogas duras, ya no podíamos hacer nada por él.",
  "Mi esposo bebía todos los días desde temprano y estaba destruyendo a la familia.",
  "Yo misma estaba en la cocaína y no podía parar, había perdido el trabajo y a mis amigos.",
  "Mi hermana tenía una adicción al juego, empeñó todas nuestras cosas dos veces.",
  "Un sobrino cayó en el fentanilo, ya lo habíamos hospitalizado dos veces por sobredosis.",
  "El maestro me dijo que la adicción era el síntoma, que había algo espiritual detrás.",
  "A los veintidós días del trabajo mi hijo pidió por su cuenta ir a rehabilitación.",
  "Mi esposo dejó el alcohol de un día para otro, ya no lo pide y ya no lo huele con ganas.",
  "Yo dejé la cocaína completamente, ahora hago ejercicio y tengo trabajo nuevo.",
  "Mi hermana se acercó a Alcohólicos Anónimos por sí sola y lleva ocho meses limpia.",
  "Mi sobrino salió del hospital y desde entonces vive con nosotros, y va bien, poco a poco.",
  "El maestro le hizo el trabajo sin necesidad de que él estuviera de acuerdo, y funcionó.",
  "Ya no despierto con el terror de qué encontraré cuando revise el celular.",
  "Mi papá dejó de perder los sueldos completos en la cantina, ahora aporta a la casa.",
  "Los cigarros mi mamá los dejó después de cuarenta años fumando, sin explicación médica.",
  "Un año en tratamiento y ni una recaída, el mismo médico me dice que es raro.",
  "El deseo se le fue solito, así lo describió él, ya no siente el llamado.",
  "Mi cuñado devolvió el dinero que había estado robando en su trabajo, un peso enorme le salió del alma.",
  "La familia se juntó otra vez después de años de peleas por su adicción.",
  "Ya no hay botellas escondidas por toda la casa, ya no encuentro nada, es paz.",
  "Mi hijo terminó la carrera después de dos años perdido, hoy trabaja y ayuda a la casa.",
  "Mi esposa se levantó una mañana y me dijo, no quiero más, y no ha vuelto a probar en catorce meses.",
  "Salimos adelante como familia, con el maestro guiándonos, es la mejor decisión que tomé.",
];
export const BODY_AMOR = [
  "Mi esposo se había ido con otra hace nueve meses, ni siquiera me dejaba explicarle nada.",
  "Llevaba cinco años esperando que mi novia me pusiera fecha, cada vez me sentía más lejos.",
  "El amor de mi vida estaba prometido con otra persona y la boda era en dos meses.",
  "Después de un divorcio muy feo pensé que nunca volvería a confiar en un hombre.",
  "La persona que amaba estaba enredada con alguien tóxico y no lograba salirse.",
  "El maestro me dijo que había un amarre que no venía de mí, sino puesto contra la relación.",
  "A las cuatro semanas del trabajo, mi esposo volvió a la casa por su cuenta, sin buscar drama.",
  "Mi novia me pidió matrimonio dos meses después del trabajo, en una carta a mano.",
  "El amor de mi vida rompió su compromiso a la semana del trabajo y me buscó a mí.",
  "Conocí a un hombre extraordinario en el aeropuerto, hoy vivimos juntos y esperamos un bebé.",
  "Aquella persona que amaba salió sola de esa relación tóxica y hoy estamos empezando algo lindo.",
  "Mi ex regresó pidiendo perdón de una manera muy sincera, no la típica de siempre.",
  "Empezó a mandarme flores todos los viernes sin explicación, después me dijo que sintió una fuerza extraña.",
  "Recuperé la comunicación con la persona que quería, hoy hablamos todos los días.",
  "Mi esposo dejó de mentir de un momento a otro, se abrió conmigo como no lo había hecho en años.",
  "Nos casamos por civil un martes lluvioso, sin invitados, y fue el día más lindo de mi vida.",
  "Mi pareja regresó con un compromiso de fidelidad que antes se negaba a firmar.",
  "El maestro me dijo el día exacto en que iba a llamar y llamó, no me lo esperaba nada.",
  "Ese hombre casado terminó con su esposa y hoy vivimos juntos, sin culpas ni escondidas.",
  "Mi esposa dejó al amante y volvió a la casa por su decisión, no la mía.",
  "Vino a mi puerta con dos maletas y me dijo, quiero intentarlo de nuevo, y aquí estamos.",
  "Después del trabajo empecé a soñar con esa persona y la soñé exactamente como la vi la primera vez.",
  "Estamos juntos hace ya siete meses y nunca me había sentido tan tranquila con alguien.",
];
export const BODY_MILAGRO = [
  "Mi mamá tenía un caso legal por herencia que llevaba trece años estancado, no había forma.",
  "Iba a perder la custodia de mis hijos por un juicio injusto que mi ex me armó.",
  "Necesitaba una visa que me habían negado cinco veces, ya me habían dicho que no siguiera intentando.",
  "Mi hermana llevaba seis años sin poder cruzar a Estados Unidos y aquí estaba nuestro papá enfermo.",
  "Un juicio de responsabilidad me tenía casi arruinado, los abogados no veían salida.",
  "El maestro me dijo que había un bloqueo puesto por alguien de la familia, algo muy raro.",
  "El caso de herencia lo resolvieron a favor de mi mamá al mes de terminado el trabajo.",
  "La custodia me la devolvieron completa, los jueces se enteraron de las mentiras de mi ex.",
  "La visa salió aprobada al sexto intento, sin explicación oficial de por qué esta sí.",
  "Mi hermana consiguió el permiso para venir y alcanzó a despedirse de mi papá antes de que se fuera.",
  "El juicio terminó a mi favor con condenatoria a la contraparte, algo que ni mi abogado esperaba.",
  "Recuperé propiedades familiares que habían usurpado décadas atrás, después de firmas milagrosas.",
  "Mi papeleo de divorcio se resolvió en semanas cuando llevaba cuatro años estancado.",
  "Una beca completa para mi hija apareció justo cuando pensábamos que se quedaba sin universidad.",
  "El seguro pagó la casa dañada después de dos años de negarse rotundamente.",
  "Un familiar apareció en un lugar donde llevábamos años pidiendo respuestas.",
  "Se me destrabó un contrato multimillonario que llevaba años en negociación estancada.",
  "Una demanda millonaria que iba a hundirme se resolvió por mediación en tres semanas.",
  "Encontré un empleo estable cuando todos los caminos parecían cerrados.",
  "Un familiar del extranjero se acordó de nosotros y nos ayudó cuando más lo necesitábamos.",
  "Ganamos una lotería pequeña justo el mes que la necesitábamos para cerrar la deuda.",
  "Se cayó una acusación falsa en mi contra que estaba a punto de meterme a la cárcel.",
  "Un policía nos avisó de un asalto planeado y pudimos evitarlo, sentí que fue una protección extra.",
];

// Openings — Spanish, gender-aware.
// Prime pool size 41 each (coprime with all body pools).
export const OPENINGS_ES_FEM = [
  "Yo llevaba años cargando este dolor sola, hasta que una amiga me habló del maestro Cris.",
  "Escribo esto todavía con lágrimas en los ojos porque no puedo creer lo que viví.",
  "Nunca creí en estas cosas, soy una mujer católica de la vieja escuela, pero llegué a un punto en que ya no tenía a quién más pedirle.",
  "Yo soy enfermera, trabajo con datos, con números, y ni así tenía explicación para lo que me pasaba.",
  "Cuando le mandé el primer mensaje al maestro me temblaban las manos, no sabía ni qué escribir.",
  "Tengo cuarenta y siete años y jamás había buscado a nadie así, pero la vida me obligó.",
  "Mi comadre me insistió durante meses hasta que le hice caso, y hoy le doy gracias todos los días.",
  "Yo trabajo en una escuela primaria y le he contado a varias compañeras porque me cambió la vida.",
  "Vengo de una familia muy conservadora, mi mamá jamás debe saber que lo consulté.",
  "Le escribí de madrugada, cuando el silencio de la casa ya me estaba matando.",
  "Después de tres psicólogas y dos psiquiatras que no me ayudaron con lo mío, me armé de valor.",
  "Yo soy divorciada, tengo dos hijos y sostengo la casa sola, no había margen para más problemas.",
  "Al principio pensé que era otro fraude de internet, esos que abundan, pero algo me hizo insistir.",
  "Mi hermana mayor fue la que me llevó de la mano, ella ya había ido antes.",
  "Yo lo escuché por primera vez en un TikTok mientras planchaba, me hizo pausa y lo escuché completo.",
  "Le hablé el mismo día del cumpleaños número cuarenta de mi esposo, sentí que era el momento.",
  "Cuando pisé por primera vez su lugar sentí una paz que hace mucho no sentía en ningún lado.",
  "Yo trabajo de comerciante en el mercado, no tengo estudios, y aún así me trató con muchísimo respeto.",
  "Cargaba una tristeza que ya me estaba enfermando físicamente, se me caía el cabello y todo.",
  "Estaba desesperada porque los caminos ya no se veían, todos cerrados, y una vecina me habló de él.",
  "Vengo de la sierra de Chihuahua, jamás pensé conectarme con alguien de tan lejos.",
  "Mi mejor amiga me dijo, dale una oportunidad, y como ya no tenía nada que perder, acepté.",
  "Yo soy costurera, tengo mi pequeño taller, y llegó un momento en que ni las manos me respondían.",
  "Le escribí al maestro un lunes en la mañana, después de una noche de mucho llanto.",
  "En una clase de yoga escuché a otra alumna contando su historia y me quedé con el nombre.",
  "Yo soy contadora, tengo mi despacho, y aún así lo espiritual me superó por completo.",
  "Un domingo por la tarde vi un video suyo por casualidad, sentí que hablaba directo conmigo.",
  "Cuando lo llamé por primera vez tenía la fiebre alta, ni sé cómo pude sostener la llamada.",
  "Mi hija adolescente me insistió, ella lo había visto en redes sociales, y me dio confianza.",
  "Yo llegué desde otro país, no conocía a nadie, y él fue quien me guió cuando más lo necesité.",
  "Vengo de un matrimonio de veinte años que se me estaba desmoronando y no sabía qué hacer.",
  "Mi papá acababa de morir cuando lo contacté, todavía no me recuperaba de eso.",
  "Le hablé mientras cocinaba, con el celular en la oreja y el mole hirviendo, algo desesperada.",
  "En un cumpleaños familiar una prima se abrió y contó su experiencia con él, me sacudió.",
  "Yo lo pensé mucho antes de llamar, pesaba mi crianza religiosa, pero al final gana el instinto de madre.",
  "Trabajo en una farmacia y una clienta habitual me habló de él con toda la confianza del mundo.",
  "Después de perder mi negocio y quedar en la calle, ya no había orgullo que me detuviera.",
  "Cuando le escribí por primera vez no tenía ni idea de cómo funcionaba, y él fue muy paciente conmigo.",
  "Yo estaba pasando por una menopausia muy pesada, y encima todos los problemas juntos.",
  "Le hablé el mismo día que le dieron el resultado del examen a mi hija, no aguantaba la incertidumbre.",
  "Mi consultora espiritual anterior murió, y de pura casualidad llegué al maestro, o de casualidad no fue.",
];

export const OPENINGS_ES_MASC = [
  "Yo soy hombre, no acostumbro escribir sobre lo espiritual, pero lo que viví me obliga a compartirlo.",
  "Llegué al maestro Cris después de tres años intentando arreglar solo lo que ya no podía.",
  "Cuando le escribí por primera vez llevaba semanas sin dormir bien, la desesperación me tenía loco.",
  "Yo trabajo en el campo, no soy de esas cosas, pero mi señora insistió y le hice caso al fin.",
  "Fui muy escéptico al principio, soy ingeniero, pero los hechos hablan más fuerte que mis dudas.",
  "Tengo cincuenta y dos años, familia grande, y jamás pensé que iba a acabar buscando ayuda así.",
  "Un compañero de trabajo me habló bien de él, sin insistir, y eso me dio confianza.",
  "Soy chofer de trailer, cruzo carretera dos veces por semana, y el estrés me estaba matando.",
  "Yo soy del campo de Zacatecas, no leo mucho de estos temas, pero mi vida cambió porque me arriesgué.",
  "Le mandé mensaje una madrugada cuando ya no aguantaba lo que me estaba pasando en la casa.",
  "Después de perder mi puesto en la empresa donde llevaba diecinueve años, ya no tenía nada que perder.",
  "Yo soy padre soltero con tres hijos, no había margen para seguir dudando.",
  "Al principio no le creía a nadie, ya había pagado a varios estafadores antes.",
  "Mi hermano mayor fue quien me llevó, él ya había tenido buenos resultados con él.",
  "Vi un video suyo un domingo, mientras esperaba turno en la barbería, algo hizo clic.",
  "Le hablé después de una discusión muy fuerte con mi esposa, no me quedaba otra.",
  "Cuando llegué con el maestro sentí una calma rara, algo diferente a otros lugares que había visitado.",
  "Yo trabajo en construcción, no tengo estudios formales, y aún así me trató con respeto de igual.",
  "Cargaba una rabia que no me dejaba trabajar bien ni ser buen padre, algo estaba mal.",
  "Estaba desesperado por un problema legal muy grande, y un socio me pasó su contacto.",
  "Vengo del sur, jamás me imaginé estar hablando por videollamada con un maestro espiritual.",
  "Mi mejor amigo me dijo, mano, ya intenta esto, y le hice caso finalmente.",
  "Yo soy taxista, dieciocho años en el volante, y aún así los golpes de la vida me superaron.",
  "Le escribí en un descanso del trabajo, sentado en la banqueta con el celular en la mano.",
  "En una reunión de vecinos escuché la experiencia de otro señor y me quedé pensando en eso semanas.",
  "Yo soy comerciante, tengo mi puesto en el tianguis, y llegué con él por una racha muy mala.",
  "Un martes por la noche me tocó ver un directo suyo, y desde ahí lo empecé a seguir.",
  "Cuando lo llamé la primera vez casi cuelgo antes de que contestara, del nervio.",
  "Mi hijo mayor me insistió tanto que al final acepté por darle gusto, y salí ganando yo.",
  "Yo migré hace veinte años, y en un momento sentí que estaba lejos de todo lo que era mío, hasta de mí.",
  "Vengo de un matrimonio duro que llevaba años sostenido con alambre.",
  "Mi mamá murió y a los tres meses vino todo lo demás, negocio, salud, deudas, todo junto.",
  "Le hablé de madrugada, con la garganta apretada, sin poder ni hablar bien al principio.",
  "En un partido de futbol un compa me contó su historia con él, algo me tocó por dentro.",
  "Yo lo pensé mucho antes de llamar, ni siquiera se lo dije a mi señora al principio.",
  "Trabajo en seguridad privada y un cliente frecuente me lo recomendó con mucha seriedad.",
  "Después de que se llevaron mi camioneta y no había respuesta legal, ya no tenía orgullo.",
  "Cuando le escribí no tenía ni idea de cómo era el proceso, él me lo explicó todo paso a paso.",
  "Estaba pasando por una etapa muy oscura, hasta pensamientos malos me venían.",
  "Le hablé el día que le dijeron a mi hijo que necesitaba cirugía urgente, ya no aguanté más.",
  "Mi cuñado había ido con él, tuvo buen resultado, y me pasó el contacto sin problema.",
];

// Transformations (post-work reaction). Prime pool 43 (coprime with 41/23/47/29).
export const TRANSFORMATIONS_ES = [
  "El cambio no fue de un día para otro, fue gradual, sostenido, real.",
  "Sentí como si me hubieran quitado una capa pesada del pecho, algo físico.",
  "Empecé a dormir de corrido por primera vez en muchos meses.",
  "La casa empezó a sentirse distinta, más tranquila, más luminosa.",
  "Los sueños extraños que llevaba tiempo teniendo desaparecieron por completo.",
  "Volví a tener ganas de arreglarme, cosa que hacía tiempo no me nacía.",
  "Recuperé el apetito y bajaron los dolores de cabeza que me tenían medicada todo el tiempo.",
  "Mis hijos me notaron el cambio antes que nadie, me lo dijeron a los pocos días.",
  "Empecé a atraer buenas noticias, propuestas, oportunidades laborales de la nada.",
  "El teléfono empezó a sonar con contactos que hacía tiempo estaban perdidos.",
  "Las peleas absurdas en la familia bajaron muchísimo, sin que nadie hablara de nada.",
  "Mis amigas más cercanas me dijeron, te ves diferente, y ni yo sabía en qué exactamente.",
  "Empecé a tomar decisiones con claridad, cosa que llevaba años sin poder hacer.",
  "Las energías negativas de ciertas personas dejaron de afectarme como antes.",
  "El trabajo empezó a fluir, los clientes regresaron, los pagos llegaron a tiempo.",
  "Mi hija pequeña, que tenía miedo de ciertos rincones de la casa, empezó a jugar sin problema.",
  "El perro dejó de ladrar por las noches, algo que llevaba pasando meses sin explicación.",
  "Mi mamá, que vive con nosotros, comentó que la energía de la casa se sentía más fresca.",
  "El aire mismo del hogar se volvió otro, difícil de explicar pero yo lo sentía.",
  "Los pleitos con mi esposa bajaron muchísimo, aprendimos a hablarnos otra vez.",
  "Empecé a ir al gimnasio de nuevo después de dos años sin hacer nada de ejercicio.",
  "Recuperé amistades importantes que se habían enfriado sin razón.",
  "El dinero comenzó a alcanzarme, no cambió mucho el ingreso pero sí la administración.",
  "Volvieron a llegarme las oraciones, algo que había perdido con tanto dolor.",
  "Empecé a reírme de tonterías, algo que llevaba meses sin hacer con ganas.",
  "Las visitas a la casa volvieron, sobre todo la familia que se había ido alejando.",
  "Tuve más paciencia con mis alumnos, algo que había perdido con tanto estrés.",
  "Bajé de peso sin dieta, era el estrés que se me había ido acumulando en el cuerpo.",
  "La ansiedad matutina bajó a niveles muy manejables, casi de repente.",
  "Cambió mi rostro, hasta las fotos me lo confirman, se me nota más luz.",
  "Los vicios de mi pareja fueron bajando sin que le dijera nada, como por si solo.",
  "Volví a soñar cosas bonitas, hasta con mis abuelos que ya no están, en paz.",
  "Los niños volvieron a jugar en el patio como antes, sin miedo de nada.",
  "Empecé a orar de nuevo con más ganas, con más claridad de a quién le hablaba.",
  "Recuperé el olfato para los negocios, la intuición volvió con fuerza.",
  "El cambio se notó en mi rendimiento en el trabajo, hasta mi jefe me felicitó.",
  "Volví a tener energía para cocinar, cosa que había perdido con la tristeza.",
  "El teléfono dejó de estar lleno de números desconocidos y llamadas raras.",
  "Empecé a caminar más recto, con más aire, con más presencia, sin darme cuenta.",
  "Recobré el enfoque para terminar cosas que llevaban años pendientes.",
  "Los ataques de pánico se espaciaron, y a los tres meses ya no me daban.",
  "Volvió el deseo, la ilusión, la sonrisa espontánea, cosas de las que hace mucho no me daba cuenta.",
  "El silencio de la casa dejó de sentirse pesado, ahora es un silencio tranquilo.",
];

// Closings / gratitude. Prime pool 47.
export const CLOSINGS_ES = [
  "Gracias maestro, hasta el último día de mi vida se lo voy a agradecer.",
  "Recomiendo al maestro Cris con los ojos cerrados a cualquiera que esté sufriendo como yo sufrí.",
  "Si estás dudando, no lo pienses tanto, arriésgate como me arriesgué yo.",
  "Este mensaje lo escribo con lágrimas en los ojos y el corazón lleno.",
  "Que Dios te bendiga siempre, maestro, tú fuiste el instrumento pero fue Él quien obró.",
  "Yo ya no soy la misma persona, y todos en la familia lo notan.",
  "A quien lea esto, pruébenlo, no se van a arrepentir jamás.",
  "No tengo palabras, sólo agradecimiento infinito.",
  "Volvería mil veces con el maestro sin dudarlo, ya me tocó y me tocará seguir.",
  "Mi familia entera está agradecida, especialmente mi mamá que fue la más dolida con todo esto.",
  "Cristopher, no imagina el bien que hace, mil bendiciones y salud siempre.",
  "Hoy vivo tranquila, cosa que hacía años pensaba imposible.",
  "Gracias por devolvernos la paz a mi casa, no hay pago que alcance para eso.",
  "Ya llevo un año con los cambios firmes, no fue de solo una temporada.",
  "El maestro es una persona muy sencilla, muy humano, muy respetuoso, eso también hay que decirlo.",
  "Sigo en contacto con él para lecturas periódicas y cada una es exacta.",
  "Le mandé referidos a otras personas de mi trabajo, todos han quedado bien.",
  "Bendito sea Dios por poner al maestro en mi camino.",
  "Yo dudé mucho antes de escribir esto público, pero le prometí al maestro que dejaría testimonio.",
  "No se dejen convencer por gente que dice que estas cosas son mentira, mi vida es la prueba.",
  "Le mando un abrazo desde acá, maestro, y muchas oraciones por su vida.",
  "Un día iré a Guaymas a agradecerle en persona, es la promesa que me hice.",
  "Gracias por tratarnos con humanidad, sin cobros disparados, con paciencia y sinceridad.",
  "Cada aniversario del trabajo prendo una vela y doy gracias al cielo.",
  "Le pedí al maestro que me acompañe con lecturas cada seis meses, y aquí seguimos.",
  "Ya somos más de la familia consultando, mis primas también han quedado impresionadas.",
  "El maestro es serio, no promete cosas que no puede cumplir, eso lo distingue.",
  "Mi historia parece de novela pero yo la viví, y la sigo viviendo, tranquila y bendecida.",
  "Cuido lo que Dios y el maestro me devolvieron, con oración y buenas obras diarias.",
  "Prometo hacer mi parte de lo pactado, hasta el último día.",
  "Le tengo un altar chico en la casa, con su foto y una vela, y ahí me acuerdo de él.",
  "Cristopher, gracias por creerme cuando ni yo me creía a mí misma.",
  "Esta experiencia me marcó, y voy a ir con calma pero constante, respetando todo.",
  "Que el cielo te devuelva multiplicado el bien que nos hiciste, maestro.",
  "Ya llevo tres años de mejoría constante, no fue moda ni casualidad.",
  "Le tengo a mi maestro guardado en el corazón, y él lo sabe.",
  "A cualquier persona que necesite, no lo pienses tanto, escríbele, es real.",
  "Este es un testimonio pequeño comparado con todo lo que él me devolvió.",
  "Gracias por darme mi vida de vuelta, sin exagerar ni una sola palabra.",
  "Mi esposo también agradece, aunque él nunca se meta con estas cosas por su carácter.",
  "Que Dios te siga usando como Su instrumento, maestro, aún hay muchas familias que necesitan.",
  "Aquí estoy, feliz, en paz, y con historia para contar hasta el día que me toque irme.",
  "Cristopher es un ser de luz, todos los que hemos ido lo confirmamos.",
  "Mi mamá dice que después del trabajo la casa se convirtió en el hogar que siempre soñó tener.",
  "Yo sólo puedo decirle mil veces gracias, con el corazón, sin adornos.",
  "Que este testimonio le sirva a otros, ese es mi único fin al escribirlo.",
  "Bendiciones eternas para ti, maestro, y para tu familia también.",
];

// English pools (~20% of comments are EN). Smaller but still prime-sized.
export const OPENINGS_EN_FEM = [
  "I never thought I would write a testimonial like this, but here I am.",
  "I contacted Cris because I was running out of options and my heart was breaking.",
  "A dear friend of mine recommended him after her own miracle, and I trusted her.",
  "I was skeptical at first because I had been scammed before online.",
  "As a woman raised in a strict religious home, this was not my usual choice.",
  "I remember the day I first messaged him, my hands were shaking.",
  "I am a nurse and I only trust data, but nothing else worked.",
  "My mother-in-law was the one who insisted I reached out.",
  "I saw one of his videos late at night and something felt different.",
  "After years of therapy that did not touch my real problem, I gave this a try.",
  "I was on the verge of losing everything when I found him.",
  "I am a working mother, no time to waste, and I needed real answers.",
  "The first call I made was already at midnight and full of tears.",
  "I had lost hope, honestly, and only wanted someone to hear me.",
  "My sister flew from Texas to hold my hand during the first call.",
  "I found his profile through a comment from another woman like me.",
  "As a professional, I was afraid of judgment, but he never made me feel that way.",
  "I write this today with peace in my heart, something I could not imagine before.",
  "It was a Sunday morning when I finally said, I will trust this man.",
  "I want other women to know there is still real spiritual help out there.",
];
export const OPENINGS_EN_MASC = [
  "I do not usually write online, but I owe this testimonial to Cris.",
  "As a father of three, I was carrying too much and needed real help.",
  "A coworker mentioned Cris and I ignored it for months, until I could not anymore.",
  "I am a private person, but this experience deserves to be shared.",
  "I served in the military and I do not scare easy, but this situation broke me.",
  "It took me weeks to build the courage to send that first message.",
  "I lost my job and my father the same month, and everything else followed.",
  "As an engineer, I resisted this for a long time, but the facts speak louder.",
  "My wife dragged me to make the call, and I am so grateful she did.",
  "I found him through a very small YouTube video that got me curious.",
  "I was on the edge and had nothing left to lose.",
  "I am a small business owner and my company was drowning.",
  "The first video call I had with him lasted almost two hours.",
  "As a man raised to solve things alone, this was hard for me to accept.",
  "I contacted him for my son, not for myself, but the whole family was blessed.",
  "I was raised catholic and this stretched my beliefs, but I am glad I opened up.",
  "It was after a car accident that I called him, not really thinking straight.",
  "I only spoke to my brother about this, no one else in the family knew.",
  "The night before I called, I could not sleep and something told me to search.",
  "I remember the exact moment I decided to trust him, and it saved us.",
];
export const BODY_EMBARAZO_EN = [
  "We tried for nine years to conceive and nothing worked, not IVF, not anything.",
  "The doctors said my chances were near zero and to consider adoption.",
  "After two miscarriages I was emotionally destroyed and had given up.",
  "Cris told me there was a heavy envy from a close family member.",
  "Six weeks after the work my test came back positive and I cried for two days.",
  "My baby was born healthy in October and we named her Grace, in gratitude.",
  "The specialists could not explain how after twelve years of infertility I conceived naturally.",
];
export const BODY_SALUD_EN = [
  "I was diagnosed with stage three breast cancer and the outlook was grim.",
  "My father was in a coma for seventeen days and doctors asked us to let him go.",
  "My kidney levels were failing and I was three weeks from starting dialysis.",
  "Cris told me there was an ancestral bond that needed to be broken first.",
  "After the work, my last MRI came back clear and the doctors were stunned.",
  "My father opened his eyes on the third day, I have no other way to explain it.",
  "My lupus flare-ups stopped, something my rheumatologist still can not fully explain.",
];
export const BODY_QUIEBRA_EN = [
  "I owed more than eighty thousand dollars in credit card debt and my house was in foreclosure.",
  "My restaurant had closed and I was two weeks from declaring bankruptcy.",
  "I had lost my job in the pandemic and my savings were gone from medical bills.",
  "Cris told me the professional envy in my workplace had built walls around me.",
  "Ten days after the work an old client called me with a huge contract, it saved me.",
  "I got my old position back after three years unemployed, something I never expected.",
  "A silent partner appeared out of nowhere and today my business has three branches.",
];
export const BODY_DESAPARECIDO_EN = [
  "My teenage son ran away from home and we had no news for three weeks.",
  "My brother had been missing for nine months, the police stopped answering me.",
  "The cartel took my father and we lived two months in silence, not knowing.",
  "Cris told me he was alive and gave me details I could not have known.",
  "On the twelfth day my son called crying from a payphone in another state.",
  "My brother walked home one morning, thinner, but complete.",
  "My father was found after three days of the work, exactly where Cris described.",
];
export const BODY_PROTECCION_EN = [
  "My daughter was being stalked by a man from her school on her way home.",
  "A neighbor threatened my family with weapons over a stupid fence dispute.",
  "A jealous ex was showing up at my house every night, I was terrified.",
  "Cris said the energy was very heavy and the whole house needed cleansing.",
  "Within a month my stalker moved to another city with no explanation.",
  "The neighbor who threatened us had an accident and never came back to bother us.",
  "The workplace harassment case was closed for lack of evidence, I came out clean.",
];
export const BODY_ADICCION_EN = [
  "My son had been on hard drugs for three years and we were losing him.",
  "My husband drank every day from morning to night and the family was falling apart.",
  "My daughter was gambling everything away, twice she pawned my jewelry.",
  "Cris told me the addiction was a symptom of something spiritual behind it.",
  "Twenty two days after the work my son asked to go to rehab on his own.",
  "My husband stopped drinking overnight, thirty years of drinking, gone.",
  "My sister joined Alcoholics Anonymous on her own and has been sober eight months.",
];
export const BODY_AMOR_EN = [
  "My husband had left me nine months ago for another woman and refused to talk to me.",
  "The love of my life was engaged to someone else, the wedding was in two months.",
  "After an ugly divorce I thought I would never trust a man again.",
  "Cris told me the block was not mine, but placed by someone against my relationship.",
  "Four weeks after the work, my husband came home on his own with no drama.",
  "My love broke off his engagement a week after the work and came back to me.",
  "I met an amazing man at the airport, today we live together and expect our first child.",
];
export const BODY_MILAGRO_EN = [
  "A legal case my mother had been fighting for thirteen years finally moved.",
  "I was about to lose custody of my kids to my ex in a rigged trial.",
  "I needed a US visa that had been denied five times, they told me not to try again.",
  "Cris told me there was a family curse blocking every path.",
  "The visa was approved on the sixth attempt with no explanation.",
  "The custody was returned to me in full, my ex was exposed in court.",
  "I got a full scholarship for my daughter that appeared out of nowhere.",
];
export const TRANSFORMATIONS_EN = [
  "The change was not overnight, it was gradual and steady, very real.",
  "I felt like a heavy layer had been lifted from my chest, something physical.",
  "I began to sleep through the night for the first time in months.",
  "My home started to feel calmer, brighter, different in a good way.",
  "The strange dreams I had been having disappeared completely.",
  "I got my appetite back and the constant headaches faded.",
  "My kids noticed the change before I did, they told me within days.",
  "Good news started coming, job offers, deals, opportunities out of nowhere.",
  "Calls started coming from contacts I had lost years ago.",
  "Silly family fights simply decreased, no one had a big talk about it.",
  "My closest friends said I looked different, and I could not explain it either.",
  "I began making clear decisions again, something I had not been able to do.",
  "Certain people’s negative energy stopped affecting me the way it used to.",
  "Work started flowing, clients came back, payments arrived on time.",
  "My younger daughter, who used to fear certain corners of the house, played freely again.",
];
export const CLOSINGS_EN = [
  "Thank you Cris, I will thank you for the rest of my life.",
  "I recommend Cris with my eyes closed to anyone suffering like I was.",
  "If you are hesitating, take the risk, like I did.",
  "I am writing this with tears in my eyes and a full heart.",
  "God bless you always, master, you were the vessel of His grace.",
  "I am not the same person, and my whole family notices it.",
  "To anyone reading this, try it, you will not regret it.",
  "I have no words, only endless gratitude.",
  "I would go back to Cris a thousand times without hesitation.",
  "My family is grateful, especially my mother who suffered the most with all of this.",
  "Cristopher, you have no idea how much good you do, may health and blessings follow you.",
  "Today I live at peace, something I thought was impossible for years.",
  "Thank you for giving my home its peace back, no payment could ever match that.",
  "One year later the changes are still solid, this was not a temporary thing.",
  "I keep in contact with him for periodic readings and each one is precise.",
];

// Regional flavor micro-phrases by country style. Added optionally to
// ~40% of comments to make the modismo feel authentic without turning
// the testimonial into a caricature.
export const REGIONAL_FLAVOR = {
  MX: ["neta", "en serio", "cañón", "de a de veras", "ya la había regado bastante", "gacho"],
  AR: ["en serio", "posta", "una bocha", "re bien", "bárbaro"],
  CO: ["parcero", "de una", "chévere", "berraco", "juepucha"],
  CL: ["cachái", "buena onda", "pucha", "la raja"],
  PE: ["chévere", "una nota", "pata", "pisos"],
  VE: ["chamo", "una vaina rara", "en serio"],
  ES: ["de verdad", "vale", "menuda historia", "una pasada"],
  US_ES: ["la verdad", "de a de veras", "en serio"],
  DEFAULT: ["de verdad", "en serio"],
};

// Enum-based pick: pool[(i * mult) mod pool.length] — mult must be coprime with pool.length.
export const enumPick = (pool, i, mult) =>
  pool[((i * mult) % pool.length + pool.length) % pool.length];

// Deterministic small hash for micro-decisions (branching, sprinkling flavor).
export const smallHash = (i, mult) => Math.abs((i * mult) | 0);
